import { useState, useEffect } from 'react';
import { X, Loader2, History, User, ChevronDown, ChevronUp } from 'lucide-react';
import { getAuditLogs } from '../lib/admin';
import toast from 'react-hot-toast';

type AuditLogRecord = {
  id: string;
  action: 'INSERT' | 'UPDATE' | 'DELETE' | string;
  table_name: string;
  created_at: string;
  record_id?: string | null;
  old_data?: Record<string, unknown> | null;
  new_data?: Record<string, unknown> | null;
  changed_by?: string | null;
};

interface AuditLogViewerProps {
  onClose: () => void;
}

export function AuditLogViewer({ onClose }: AuditLogViewerProps) {
  const [logs, setLogs] = useState<AuditLogRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [tableFilter, setTableFilter] = useState<string>('all');
  const [expandedLog, setExpandedLog] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    getAuditLogs(100)
      .then((data) => {
        if (mounted) setLogs(data as AuditLogRecord[]);
      })
      .catch((error) => {
        if (!mounted) return;
        console.error('Error loading audit logs:', error);
        toast.error('Failed to load audit logs');
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'INSERT':
        return 'bg-green-500/10 text-green-400';
      case 'UPDATE':
        return 'bg-blue-500/10 text-blue-400';
      case 'DELETE':
        return 'bg-red-500/10 text-red-400';
      default:
        return 'bg-gray-500/10 text-gray-400';
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesAction = actionFilter === 'all' || log.action === actionFilter;
    const matchesTable = tableFilter === 'all' || log.table_name === tableFilter;
    return matchesAction && matchesTable;
  });

  const uniqueTables = Array.from(new Set(logs.map(log => log.table_name)));

  const toggleLogExpansion = (logId: string) => {
    setExpandedLog(expandedLog === logId ? null : logId);
  };

  const getChangeSummary = (log: AuditLogRecord) => {
    if (log.action === 'DELETE') {
      return `Deleted record from ${log.table_name}`;
    }
    if (log.action === 'INSERT') {
      return `Created new record in ${log.table_name}`;
    }
    if (log.action === 'UPDATE' && log.old_data && log.new_data) {
      const changes = [];
      for (const key in log.new_data) {
        if (log.old_data[key] !== log.new_data[key]) {
          changes.push(key);
        }
      }
      return changes.length > 0
        ? `Updated ${changes.slice(0, 3).join(', ')}${changes.length > 3 ? '...' : ''}`
        : 'Updated record';
    }
    return 'Modified record';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-scale-navy rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col border border-gray-800">
        <div className="p-6 border-b border-gray-800 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-scale-blue/10 rounded-full flex items-center justify-center">
              <History className="w-6 h-6 text-scale-blue" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Audit Log</h2>
              <p className="text-sm text-gray-400">View all system activity and changes</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 border-b border-gray-800 flex-shrink-0">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Filter by Action
              </label>
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="w-full px-3 py-2 bg-scale-darker border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-scale-blue"
              >
                <option value="all">All Actions</option>
                <option value="INSERT">Insert</option>
                <option value="UPDATE">Update</option>
                <option value="DELETE">Delete</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Filter by Table
              </label>
              <select
                value={tableFilter}
                onChange={(e) => setTableFilter(e.target.value)}
                className="w-full px-3 py-2 bg-scale-darker border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-scale-blue"
              >
                <option value="all">All Tables</option>
                {uniqueTables.map(table => (
                  <option key={table} value={table}>{table}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-scale-blue animate-spin" />
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <History className="w-12 h-12 text-gray-600 mb-4" />
              <p className="text-gray-400">No audit logs found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredLogs.map((log) => (
                <div
                  key={log.id}
                  className="bg-scale-darker rounded-lg border border-gray-700 overflow-hidden"
                >
                  <div
                    className="p-4 cursor-pointer hover:bg-scale-dark transition-colors"
                    onClick={() => toggleLogExpansion(log.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${getActionColor(log.action)}`}>
                            {log.action}
                          </span>
                          <span className="text-gray-400 text-sm">{log.table_name}</span>
                          <span className="text-gray-500 text-xs">{formatDate(log.created_at)}</span>
                        </div>
                        <p className="text-white text-sm mb-1">{getChangeSummary(log)}</p>
                        {log.record_id && (
                          <p className="text-gray-500 text-xs font-mono">
                            Record ID: {log.record_id.substring(0, 8)}...
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {expandedLog === log.id ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {expandedLog === log.id && (
                    <div className="p-4 bg-scale-dark border-t border-gray-700 space-y-3">
                      {log.old_data && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-400 mb-2">Previous Data:</h4>
                          <pre className="text-xs bg-scale-darker p-3 rounded overflow-x-auto text-gray-300 border border-gray-700">
                            {JSON.stringify(log.old_data, null, 2)}
                          </pre>
                        </div>
                      )}
                      {log.new_data && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-400 mb-2">New Data:</h4>
                          <pre className="text-xs bg-scale-darker p-3 rounded overflow-x-auto text-gray-300 border border-gray-700">
                            {JSON.stringify(log.new_data, null, 2)}
                          </pre>
                        </div>
                      )}
                      <div className="flex items-center space-x-2 text-xs text-gray-500">
                        <User className="w-3 h-3" />
                        <span>Changed by: {log.changed_by?.substring(0, 8)}...</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-800 flex justify-between items-center flex-shrink-0">
          <p className="text-sm text-gray-400">
            Showing {filteredLogs.length} of {logs.length} log entries
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
